import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import type {
  SingleCamAgentPoolListResponseDto,
  SingleCamAgentPoolMemberDto,
} from '@/types/dtos/singlecam-agent/singlecam-agent-pool.dto';

export const LIST_SINGLECAM_AGENT_POOL_QUERY_KEY = 'singlecam-agent-pool';

export function filterSingleCamAgentsByStatus(
  agents: SingleCamAgentPoolMemberDto[],
  filter: string,
): SingleCamAgentPoolMemberDto[] {
  switch (filter) {
    case 'connected':
      return agents.filter((a) => a.isConnected);
    case 'offline':
      return agents.filter((a) => !a.isConnected);
    case 'allocated':
      return agents.filter((a) => Boolean(a.allocatedStagePlayerId));
    case 'free':
      return agents.filter((a) => !a.allocatedStagePlayerId);
    case 'maintenance':
      return agents.filter((a) => a.opsStatus === 'maintenance' || a.maintenanceMode);
    case 'paused':
      return agents.filter((a) => a.opsStatus === 'paused');
    default:
      return agents;
  }
}

const listSingleCamAgentPool = async (token: string): Promise<SingleCamAgentPoolMemberDto[]> => {
  const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/pool`;
  const response = (await sendJSONRequest({ url, method: METHOD.GET, token })) as SingleCamAgentPoolListResponseDto;
  return (response.agents ?? []).map((a) => ({
    ...a,
    deployment: a.deployment ?? (a.vms?.length ? 'azure' : 'standalone'),
    opsStatus: a.opsStatus ?? 'active',
    vms: a.vms ?? [],
  }));
};

export function useListSingleCamAgentPoolQuery(options?: { enabled?: boolean }) {
  const { getToken } = useAuth();
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: [LIST_SINGLECAM_AGENT_POOL_QUERY_KEY],
    queryFn: async () => {
      const token = await getToken();
      return listSingleCamAgentPool(token ?? '');
    },
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}
